import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Provider } from 'react-redux';
import store from './state/store';
import ProtectedRoute from './ProtectedRoute';
import ETLWorkflow from './workflows/ETLWorkflow';
import LoginPage from './Pages/loginPage/LoginPage';
import SignupPage from './Pages/signupPage/SignupPage';
import PasswordPage from './Pages/forgotPassword/PasswordPage';
import DashboardLayout from './Pages/landingPage/DashboardLayout';
import Header from './Pages/landingPage/Header';
import Connector from './Pages/landingPage/Connector';
import Reports from './Pages/landingPage/Reports';
import ScheduleJob from './Pages/landingPage/ScheduleJob';
import MainpageContent from './Pages/mainPage/components/MainpageContent';
import SidebarLayout from './Pages/projectDashboard/SidebarLayout';
import MyConnections from './Pages/connectionPage/MyConnections';
import ConnectionForm from './Pages/connectionPage/CreateNewConnections';
import 'react-toastify/dist/ReactToastify.css';
import ParentComponent from './workflows/Tabs/ParentComponent';
import ManageProfile from './Pages/landingPage/ManageProfile';
import ETLMapping from './workflows/Component/Mapping';
import MappingWrapper from './workflows/MappingWrapper';
import OAuthCallback from './apis/OAuthCallback';
import HubspotCallback from './Pages/connectionPage/HubspotCallback';
import GitHubCallback from './Pages/connectionPage/GitHubCallback';
import { ThemeProvider } from './Context/ThemeContext';
import HelpdeskFloatingIcon from './components/HelpdeskFloatingIcon';
import FloatingFeedbackButton from './components/FloatingFeedbackButton';
import AutoSignOut from './components/AutoSignOut';
import Settings from './Pages/SettingPage/Setting';
import Help from './Pages/HelpPage/Help';
import Documentation from './Pages/documentationPage/Documentation';
import ContactPage from './Pages/contactPage/ContactPage';
import { ToastContainer } from 'react-toastify';
import FreeTrial from './Pages/TrialPage/FreeTrial';
import FeedbackPage from './Pages/FeedbackPage/FeedbackPage';
import ReviewDetailed from './Pages/reviewDetailedPage/ReviewDetailed';
import TrialWarningBanner from './components/TrialWarningBanner';
import AdminUsers from './admin/pages/AdminUsers';
import AdminSetting from './admin/pages/AdminSetting';
import AdminAuditLog from './admin/pages/AdminAuditLog';
import AdminAnnoucement from './admin/pages/AdminNotification';
import AdminOrganizations from './admin/pages/AdminOrganizations';
import AdminContactSubmissions from './admin/pages/AdminContactSubmissions';
import AdminPublicReviews from './admin/pages/AdminPublicReviews';
import ChangePasswordPage from './Pages/changePasswordPage/ChangePassword';

// Component to conditionally render Header based on route
const ConditionalHeader = () => {
  const location = useLocation();

  // Routes that should show the header (protected routes)
  const protectedRoutes = [
    '/userdashboard',
    '/recentworkflows',
    '/myconnections',
    '/createconnections',
    '/workflow',
    '/manageprofile',
    '/mappings',
    '/settings',
    '/help',
    '/connector',
    '/reports',
    '/schedule-job',
  ];

  // Check if current route should show header
  const shouldShowHeader = protectedRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  return shouldShowHeader ? <Header /> : null;
};

// Component to conditionally render Floating Feedback Button
const ConditionalFloatingFeedback = () => {
  const location = useLocation();

  // Protected routes where feedback button should not be visible
  const protectedRoutes = [
    '/userdashboard',
    '/recentworkflows',
    '/myconnections',
    '/createconnections',
    '/workflow',
    '/manageprofile',
    '/mappings',
    '/settings',
    '/help',
    '/connector',
    '/reports',
    '/schedule-job',
    '/admin',
    '/parentworkflow',
  ];

  // Don't show on feedback page itself
  if (location.pathname === '/feedback') {
    return null;
  }

  // Don't show on protected routes (dashboard and other internal pages)
  const isProtectedRoute = protectedRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  if (isProtectedRoute) {
    return null;
  }

  return <FloatingFeedbackButton />;
};

function App() {
  return (
    <Provider store={store}>
      {' '}
      <GoogleOAuthProvider clientId="275346719774-6tfanmnhjr0p95dqp00nu1t0j5j7vpd1.apps.googleusercontent.com">
        <ThemeProvider>
          <Router>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              style={{ zIndex: 9999 }}
            />
            <AutoSignOut />
            <TrialWarningBanner />
            <ConditionalHeader />

            <Routes>
              <Route path="/" element={<MainpageContent />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/trial" element={<FreeTrial />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/reviews" element={<ReviewDetailed />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgotpwd" element={<PasswordPage />} />
              <Route
                path="/userdashboard"
                element={<ProtectedRoute element={<DashboardLayout />} />}
              />
              <Route
                path="/recentworkflows"
                element={<ProtectedRoute element={<SidebarLayout />} />}
              />
              <Route
                path="/myconnections"
                element={<ProtectedRoute element={<MyConnections />} />}
              />

              <Route
                path="/admin/user"
                element={<ProtectedRoute element={<AdminUsers />} />}
              />
              <Route
                path="/admin/annoucement"
                element={<ProtectedRoute element={<AdminAnnoucement />} />}
              />
              <Route
                path="/admin/setting"
                element={<ProtectedRoute element={<AdminSetting />} />}
              />

              <Route
                path="/admin/audit-log"
                element={<ProtectedRoute element={<AdminAuditLog />} />}
              />
              <Route
                path="/admin/organizations"
                element={<ProtectedRoute element={<AdminOrganizations />} />}
              />
              <Route
                path="/admin/contact-submissions"
                element={
                  <ProtectedRoute element={<AdminContactSubmissions />} />
                }
              />
              <Route
                path="/admin/public-reviews"
                element={<ProtectedRoute element={<AdminPublicReviews />} />}
              />

              <Route
                path="/createconnections"
                element={<ProtectedRoute element={<ConnectionForm />} />}
              />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/api/v1/jira/oauth" element={<OAuthCallback />} />
              <Route
                path="/workflow"
                element={<ProtectedRoute element={<ETLWorkflow />} />}
              />
              <Route
                path="/hubspot/oauth/callback"
                element={<HubspotCallback />}
              />
              <Route
                path="/github/oauth/callback"
                element={<GitHubCallback />}
              />
              <Route
                path="/connector"
                element={<ProtectedRoute element={<Connector />} />}
              />
              <Route
                path="/reports"
                element={<ProtectedRoute element={<Reports />} />}
              />
              <Route
                path="/schedule-job"
                element={<ProtectedRoute element={<ScheduleJob />} />}
              />
              <Route
                path="/parentworkflow/:workflowName"
                element={<ProtectedRoute element={<ParentComponent />} />}
              />
              <Route
                path="/manageprofile"
                element={<ProtectedRoute element={<ManageProfile />} />}
              />
              <Route
                path="/mappings"
                element={<ETLMapping open={true} onClose={() => {}} />}
              />
              <Route
                path="/parentworkflow/:workflowName/mapping"
                element={<ProtectedRoute element={<MappingWrapper />} />}
              />
              <Route
                path="/settings"
                element={<ProtectedRoute element={<Settings />} />}
              />
              <Route
                path="/help"
                element={<ProtectedRoute element={<Help />} />}
              />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/contact" element={<ContactPage />} />
            </Routes>

            <ConditionalFloatingFeedback />
            <HelpdeskFloatingIcon />
          </Router>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </Provider>
  );
}

export default App;
